/**
 * TripDayAIAssistant - Assistant IA éditorial pour TripDay
 *
 * Modes :
 *  - full_day    (Rédiger la journée)
 *  - improve     (Améliorer le texte)
 *  - intro_only  (Introduction)
 *  - titles      (Titres et intertitres)
 *
 * Fournit :
 *  - Menu déroulant avec les 4 actions
 *  - Sélecteur média (checkbox "utiliser pour la génération IA")
 *  - Modale de confirmation si contenu existant (Remplacer / Ajouter à la suite / Annuler)
 *  - Snapshot mémoire pour "Annuler la dernière génération" (5 min)
 */
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { aiGenerateDay, getMediaList, exportTripDayPackage, importTripDayEditorial, detachMedia } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Sparkles, Loader2, Undo2, Check, X, Download, Upload, FileArchive, ChevronDown, Wand2, Trash2 } from "lucide-react";

const MODES = [
  { id: "full_day", label: "Génération rapide (brouillon)", desc: "À utiliser en dépannage uniquement — prefer le workflow ZIP." },
  { id: "improve", label: "Améliorer le texte", desc: "Reformule et fluidifie les paragraphes existants." },
  { id: "intro_only", label: "Générer une introduction", desc: "Ajoute une intro courte au début." },
  { id: "titles", label: "Proposer titres et intertitres", desc: "Suggestions non appliquées automatiquement." },
];

const SNAPSHOT_TTL_MS = 5 * 60 * 1000; // 5 min

export default function TripDayAIAssistant({ dayForm, setDayForm, tripId, dayId, onExport, onImport }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [availableMedia, setAvailableMedia] = useState([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [showTitlesResult, setShowTitlesResult] = useState(null);
  const snapshotTimerRef = useRef(null);

  // Charger vrais medias affectes a la journee (Bloc 2)
  useEffect(() => {
    if (!dayId) return;
    getMediaList({ entity_type: "trip_day", entity_id: dayId, limit: 200 })
      .then((res) => {
        setAvailableMedia(res.data || []);
        setSelectedMediaIds((res.data || []).map((m) => m.id));
      })
      .catch(() => { setAvailableMedia([]); setSelectedMediaIds([]); });
  }, [dayId]);

  useEffect(() => () => { if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current); }, []);

  const hasExisting = (dayForm.content_blocks || []).length > 0;

  const takeSnapshot = () => {
    setSnapshot({ blocks: JSON.parse(JSON.stringify(dayForm.content_blocks || [])), at: Date.now() });
    if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    snapshotTimerRef.current = setTimeout(() => setSnapshot(null), SNAPSHOT_TTL_MS);
  };

  const runGenerate = async (mode, extra = {}) => {
    if (!dayForm.id) {
      toast.error("Enregistrez d'abord la journée avant d'utiliser l'IA.");
      return;
    }
    setLoading(true);
    try {
      const payload = { mode, media_ids: selectedMediaIds, options: {}, ...extra };
      const res = await aiGenerateDay(tripId, dayForm.id, payload);
      const data = res.data || {};
      if (mode === "titles") {
        setShowTitlesResult(data);
        toast.success("Titres proposés — cliquez pour insérer.");
        setLoading(false);
        return;
      }
      takeSnapshot();
      setDayForm({ ...dayForm, content_blocks: data.content_blocks || [] });
      toast.success(
        mode === "full_day" ? "Journée rédigée par l'IA."
        : mode === "improve" ? "Texte amélioré."
        : "Introduction générée."
      );
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail || "Erreur IA";
      if (code === 409) {
        setPendingMode(mode);
        setShowOverwriteModal(true);
      } else if (code === 400) {
        toast.error(detail); // ex: "Données insuffisantes..."
      } else if (code === 502) {
        toast.error(`Fournisseur IA indisponible : ${detail}`);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
      setMenuOpen(false);
    }
  };

  const handleMode = (mode) => {
    if (mode === "improve" && !hasExisting) {
      toast.error("Aucun texte à améliorer.");
      return;
    }
    if (mode === "full_day" && hasExisting) {
      setPendingMode(mode);
      setShowOverwriteModal(true);
      setMenuOpen(false);
      return;
    }
    runGenerate(mode);
  };

  const confirmOverwrite = (action) => {
    setShowOverwriteModal(false);
    if (!pendingMode) return;
    if (action === "replace") {
      runGenerate(pendingMode, { overwrite_confirmed: true });
    } else if (action === "append") {
      runGenerate(pendingMode, { append: true });
    }
    setPendingMode(null);
  };

  const undoLast = () => {
    if (!snapshot) return;
    setDayForm({ ...dayForm, content_blocks: snapshot.blocks });
    setSnapshot(null);
    toast.success("Génération annulée — contenu précédent restauré.");
  };

  const insertTitlesToBlocks = () => {
    if (!showTitlesResult) return;
    const newBlocks = [...(dayForm.content_blocks || []), ...(showTitlesResult.content_blocks || [])];
    takeSnapshot();
    setDayForm({
      ...dayForm,
      title: showTitlesResult.title || dayForm.title,
      content_blocks: newBlocks,
    });
    setShowTitlesResult(null);
    toast.success("Titre + intertitres insérés en fin de journée.");
  };

  return (
    <div className="border border-accent/30 rounded-md bg-accent/5 p-3 space-y-3" data-testid="ai-assistant-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold text-primary">Workflow éditorial</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {snapshot && (
            <Button variant="outline" size="sm" onClick={undoLast} data-testid="ai-btn-undo">
              <Undo2 className="h-4 w-4 mr-1" /> Annuler la dernière génération
            </Button>
          )}
          <Button
            variant="default"
            size="sm"
            onClick={onExport}
            disabled={!onExport || loading}
            title="Télécharger un ZIP prêt pour ChatGPT (manifest + previews + instructions)"
            data-testid="ai-btn-prepare-package"
          >
            <FileArchive className="h-4 w-4 mr-1" /> Préparer pour rédaction (.zip)
          </Button>
          <Button variant="outline" size="sm" onClick={onImport} disabled={!onImport} data-testid="ai-btn-import">
            <Upload className="h-4 w-4 mr-1" /> Importer une rédaction
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMenuOpen(!menuOpen)}
              disabled={loading}
              data-testid="ai-btn-menu-toggle"
              title="Outils IA (génération rapide)"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
              Outils IA
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Workflow recommandé : (1) Rattachez vos médias · (2) Renseignez vos writer_notes · (3) « Préparer pour rédaction » (ZIP)
        · (4) Traitement éditorial externe (ChatGPT) · (5) « Importer une rédaction » (JSON de retour).
      </p>

      {menuOpen && (
        <div className="space-y-2 bg-white border border-border rounded p-2" data-testid="ai-menu">
          <p className="text-xs text-muted-foreground italic">
            Génération rapide — utile en dépannage. Le workflow éditorial principal reste « Préparer pour rédaction » + ChatGPT.
          </p>
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => handleMode(m.id)}
              disabled={loading || (m.id === "improve" && !hasExisting)}
              className="w-full text-left p-2 border border-border rounded hover:bg-secondary/40 hover:border-accent transition disabled:opacity-50"
              data-testid={`ai-mode-${m.id}`}
            >
              <div className="text-sm font-medium text-primary">{m.label}</div>
              <div className="text-xs text-muted-foreground">{m.desc}</div>
            </button>
          ))}
        </div>
      )}

      {availableMedia.length > 0 && (
        <details className="border border-border rounded p-2 bg-white" open>
          <summary className="text-xs font-semibold cursor-pointer">
            Médias à fournir à l'IA ({selectedMediaIds.length}/{availableMedia.length})
          </summary>
          <div className="mt-2 flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => setSelectedMediaIds(availableMedia.map(m => m.id))}
              className="text-xs underline text-primary"
              data-testid="ai-media-select-all"
            >Tout sélectionner</button>
            <button
              type="button"
              onClick={() => setSelectedMediaIds([])}
              className="text-xs underline text-muted-foreground"
              data-testid="ai-media-select-none"
            >Aucun</button>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {availableMedia.map((m) => {
              const checked = selectedMediaIds.includes(m.id);
              return (
                <label key={m.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedMediaIds([...selectedMediaIds, m.id]);
                      else setSelectedMediaIds(selectedMediaIds.filter(id => id !== m.id));
                    }}
                    data-testid={`ai-media-cb-${m.id}`}
                  />
                  <span className="truncate">{m.title || m.caption || m.id.slice(0, 8)}</span>
                </label>
              );
            })}
          </div>
        </details>
      )}

      {/* Confirmation modale pour écrasement */}
      <Dialog open={showOverwriteModal} onOpenChange={setShowOverwriteModal}>
        <DialogContent data-testid="ai-overwrite-dialog">
          <DialogHeader>
            <DialogTitle>Cette journée contient déjà du contenu</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Que souhaitez-vous faire ? Un instantané des blocs actuels sera conservé 5 min pour annuler si besoin.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => confirmOverwrite("cancel")} data-testid="ai-overwrite-cancel">
              <X className="h-4 w-4 mr-1" /> Annuler
            </Button>
            <Button variant="outline" onClick={() => confirmOverwrite("append")} data-testid="ai-overwrite-append">
              Ajouter à la suite
            </Button>
            <Button variant="destructive" onClick={() => confirmOverwrite("replace")} data-testid="ai-overwrite-replace">
              <Check className="h-4 w-4 mr-1" /> Remplacer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Résultats titres */}
      <Dialog open={!!showTitlesResult} onOpenChange={(o) => !o && setShowTitlesResult(null)}>
        <DialogContent data-testid="ai-titles-dialog">
          <DialogHeader>
            <DialogTitle>Propositions IA</DialogTitle>
          </DialogHeader>
          {showTitlesResult && (
            <div className="space-y-3 py-2">
              <div>
                <div className="text-xs text-muted-foreground">Titre éditorial proposé</div>
                <div className="font-heading text-lg text-primary">{showTitlesResult.title || "(aucun)"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Intertitres proposés</div>
                <ul className="list-disc list-inside text-sm">
                  {(showTitlesResult.content_blocks || []).map((b, i) => (
                    <li key={i}>{b.text}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTitlesResult(null)}>Fermer</Button>
            <Button onClick={insertTitlesToBlocks} data-testid="ai-titles-insert">Insérer dans la journée</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
